import { Trans, useLingui } from "@lingui/react/macro";
import { signInReturnPath } from "@rakazo/contracts";
import { analyticsScreen } from "@rakazo/core";
import { Button } from "@rakazo/ui-web";
import {
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { Navigate, Outlet, Route, Routes, useLocation, useSearchParams } from "react-router-dom";
import { LoadingState } from "./components/ai/primitives";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { PageLoading } from "./components/PageLoading";
import { authClient } from "./lib/auth";
import { fetchAuthCapabilities } from "./lib/auth-capabilities";
import { getTicketBoardEnabled, subscribeTicketBoardEnabled } from "./lib/bootstrap";
import { CommercialContext, useCommercialAccess, useCommercialMode } from "./lib/commercial-mode";
import { desktopBridge } from "./lib/desktop";
import { syncWebLogoAvatarIdentity } from "./lib/logo-avatar-activity";
import { identifyMonitoringUser } from "./lib/monitoring";
import { markAfterPaint, markOnce } from "./lib/performance";
import { lazyComponent } from "./lib/preload-recovery";
import { QueryUserProvider, setQueryUser } from "./lib/query";
import { rpc } from "./lib/rpc";
import {
  holdAnonymousGate,
  holdUnreachableGate,
  sessionGate,
  sessionRetryDelayMs,
  showSessionUnavailable,
} from "./lib/session-gate";
import { completeSsoCallback, SSO_CALLBACK_PATH } from "./lib/sso-flow";
import { DesktopGuestShell } from "./pages/DesktopGuestShell";
import { IntegrationCallbackPage } from "./pages/IntegrationCallback";
import { shellOverlaySearch } from "./pages/shell-overlay";

const SubscriptionGate = lazyComponent(() =>
  import("./components/SubscriptionGate").then((module) => module.SubscriptionGate),
);
const IntegrationSetupPage = lazyComponent(() =>
  import("./pages/IntegrationSetup").then((module) => module.IntegrationSetupPage),
);
const McpOAuthCallbackPage = lazyComponent(() =>
  import("./pages/McpOAuthCallback").then((module) => module.McpOAuthCallbackPage),
);
const ShellPage = lazyComponent(() => import("./pages/Shell").then((module) => module.ShellPage));
const AuthPage = lazyComponent(() => import("./pages/Auth").then((module) => module.AuthPage));
const PasswordResetPage = lazyComponent(() =>
  import("./pages/Auth").then((module) => module.PasswordResetPage),
);
const VerifyEmailPage = lazyComponent(() =>
  import("./pages/Auth").then((module) => module.VerifyEmailPage),
);
const OnboardingPage = lazyComponent(() =>
  import("./pages/Onboarding").then((module) => module.OnboardingPage),
);
const WelcomePage = lazyComponent(() =>
  import("./pages/Welcome").then((module) => module.WelcomePage),
);
const ArtifactsPage = lazyComponent(() =>
  import("./pages/Artifacts").then((module) => module.ArtifactsPage),
);
const BoardPage = lazyComponent(() => import("./pages/Board").then((module) => module.BoardPage));
const NavLinkPage = lazyComponent(() =>
  import("./pages/NavLinkPage").then((module) => module.NavLinkPage),
);

function SsoCallbackPage() {
  useEffect(() => {
    completeSsoCallback();
  }, []);
  return null;
}

export function App() {
  const [commercial, setCommercial] = useState(false);
  useEffect(() => {
    let active = true;
    void fetchAuthCapabilities()
      .then((capabilities) => {
        if (!active) return;
        setCommercial(Boolean(capabilities.commercial));
        if (capabilities.commercial) {
          document.title = "DreamBot";
          document
            .querySelector('meta[name="apple-mobile-web-app-title"]')
            ?.setAttribute("content", "DreamBot");
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);
  return (
    <CommercialContext.Provider value={commercial}>
      <ErrorBoundary fallback={<AppFailed />}>
        {window.location.pathname === SSO_CALLBACK_PATH ? (
          <SsoCallbackPage />
        ) : window.location.pathname === "/integrations/callback" ? (
          <IntegrationCallbackPage />
        ) : (
          <SessionApp />
        )}
      </ErrorBoundary>
    </CommercialContext.Provider>
  );
}

const MCP_OAUTH_CALLBACK_PATH = "/mcp/oauth/callback";

function SessionApp() {
  const commercial = useCommercialMode();
  const [searchParams] = useSearchParams();
  // A connector sign-in started on another device lands here signed out; finish it after sign-in.
  const signInDestination = signInReturnPath(searchParams.get("next"));
  const ticketBoardEnabled = useSyncExternalStore(
    subscribeTicketBoardEnabled,
    getTicketBoardEnabled,
    () => false,
  );
  const session = authClient.useSession();
  const { pathname, search } = useLocation();
  // A signed-out deep link such as the mobile app's usage entry comes back here after sign-in.
  const returnPath = signInReturnPath(`${pathname}${search}`);
  const signInKeepingPlace =
    returnPath === "/app" ? "/sign-in" : `/sign-in?next=${encodeURIComponent(returnPath)}`;
  const screen = analyticsScreen(pathname);
  const analyticsUserId = session.data?.user.id;
  const lastScreen = useRef<string | undefined>(undefined);
  useEffect(() => {
    // A pending session is not a sign-out; startup already tagged the signed-in user.
    if (session.isPending) return;
    void identifyMonitoringUser(Boolean(analyticsUserId));
  }, [analyticsUserId, session.isPending]);
  useEffect(() => {
    if (!analyticsUserId) {
      lastScreen.current = undefined;
      return;
    }
    if (!screen) return;
    const key = `${analyticsUserId}:${screen}`;
    if (lastScreen.current === key) return;
    lastScreen.current = key;
    void Promise.resolve()
      .then(() => rpc.analytics.screen({ screen, surface: desktopBridge() ? "desktop" : "web" }))
      .catch(() => undefined);
  }, [analyticsUserId, screen]);
  const gate = sessionGate(session);
  useLayoutEffect(() => {
    if (gate === "authenticated" || gate === "anonymous")
      syncWebLogoAvatarIdentity(analyticsUserId ?? null);
  }, [gate, analyticsUserId]);
  useEffect(() => {
    if (gate === "authenticated" || gate === "anonymous") setQueryUser(analyticsUserId ?? null);
  }, [gate, analyticsUserId]);
  const [holdingUnreachable, setHoldingUnreachable] = useState(false);
  const nextHolding = holdUnreachableGate(gate, holdingUnreachable);
  if (nextHolding !== holdingUnreachable) setHoldingUnreachable(nextHolding);
  const [holdingAnonymous, setHoldingAnonymous] = useState(false);
  const nextHoldingAnonymous = holdAnonymousGate(gate, holdingAnonymous);
  if (nextHoldingAnonymous !== holdingAnonymous) setHoldingAnonymous(nextHoldingAnonymous);

  useLayoutEffect(() => {
    if (session.isPending) return;
    markOnce("rk:renderer:session-committed");
    markAfterPaint("rk:renderer:session-painted");
  }, [session.isPending]);

  if (showSessionUnavailable(gate, nextHolding)) {
    return <SessionUnavailable refetch={session.refetch} />;
  }
  if (gate === "loading" && !nextHoldingAnonymous) {
    return <PageLoading data-rakazo-app-state="session-pending" />;
  }

  const user = session.data?.user;
  const desktopGuest = !user && Boolean(desktopBridge()) && pathname === "/app";
  return (
    <QueryUserProvider value={user?.id ?? null}>
      <div className="h-full" data-rakazo-app-state="ready">
        <Suspense fallback={<PageLoading />}>
          <Routes>
            <Route path="/" element={user ? <Navigate to="/app" replace /> : <WelcomePage />} />
            <Route
              path="/sign-in"
              element={
                user ? <Navigate to={signInDestination} replace /> : <AuthPage key="in" mode="in" />
              }
            />
            <Route
              path="/sign-up"
              element={user ? <Navigate to="/app" replace /> : <AuthPage key="up" mode="up" />}
            />
            <Route
              path="/forgot-password"
              element={
                user ? <Navigate to="/app" replace /> : <AuthPage key="forgot" mode="forgot" />
              }
            />
            <Route path="/reset-password" element={<PasswordResetPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route
              path="/subscription"
              element={
                user ? (
                  <Navigate to="/app?subscription" replace />
                ) : (
                  <Navigate to="/sign-in?next=/subscription" replace />
                )
              }
            />
            <Route path="/admin/*" element={<AdminRedirect />} />
            <Route
              path={MCP_OAUTH_CALLBACK_PATH}
              element={
                user ? (
                  <McpOAuthCallbackPage />
                ) : (
                  <Navigate to={`/sign-in?next=${encodeURIComponent(pathname + search)}`} replace />
                )
              }
            />
            <Route
              path="/integrations/setup"
              element={
                user ? (
                  <IntegrationSetupPage />
                ) : (
                  <Navigate to="/sign-in?next=/integrations/setup" replace />
                )
              }
            />
            <Route
              element={
                user ? (
                  <SubscriptionGate fallback={<PageLoading />}>
                    <WorkspaceOutlet />
                  </SubscriptionGate>
                ) : desktopGuest ? (
                  <DesktopGuestShell signInPath={signInKeepingPlace} />
                ) : (
                  <Navigate to={signInKeepingPlace} replace />
                )
              }
            >
              <Route
                path="/onboarding/:step?"
                element={commercial ? <Navigate to="/app" replace /> : <OnboardingPage />}
              />
              <Route path="/app" element={<ShellPage />} />
              <Route path="/app/g/:groupId" element={<ShellPage />} />
              <Route path="/app/artifacts" element={<ArtifactsPage />} />
              <Route path="/app/artifacts/:artifactId" element={<ArtifactsPage />} />
              {ticketBoardEnabled ? <Route path="/app/board" element={<BoardPage />} /> : null}
              <Route path="/app/x/:linkId" element={<NavLinkPage />} />
              <Route path="/app/:botId" element={<ShellPage />} />
            </Route>
          </Routes>
        </Suspense>
      </div>
    </QueryUserProvider>
  );
}

export function WorkspaceOutlet() {
  const access = useCommercialAccess();
  const { pathname, search } = useLocation();
  if (access?.enabled && access.state !== "active" && pathname !== "/app") {
    return (
      <Navigate
        replace
        to={{ pathname: "/app", search: shellOverlaySearch(new URLSearchParams(search)) }}
      />
    );
  }
  return <Outlet />;
}

// Holds the time just before leaving for /admin/. When the server answers that hop with this app
// again, the page it loads finds the mark and stays on the notice instead of leaving forever.
const ADMIN_HOP_KEY = "rakazo:admin-hop";
// A hop that reached the admin app leaves its mark in the tab, so only a page that started within
// this long of the mark counts as loaded by that hop.
const ADMIN_HOP_WINDOW_MS = 10_000;

function AdminRedirect() {
  // At the page's first location the server answered /admin with this app. It still leaves once,
  // since the admin app may be reachable now, unless our own hop loaded this page. A stale mark
  // never stops an in-app arrival.
  const atLoad = useLocation().key === "default";
  const [stay, setStay] = useState(() => atLoad && arrivedByAdminHop());
  const leaving = useRef(false);
  useEffect(() => {
    if (stay) {
      forgetAdminHop();
      return;
    }
    if (leaving.current) return;
    // Unmarked, the page this hop loads could not tell it from a loop.
    if (!markAdminHop() && atLoad) {
      setStay(true);
      return;
    }
    leaving.current = true;
    // Chromium keeps an entry's state across a same-URL replace. Clearing it drops the router key
    // that marks an in-app arrival, so the next page reads as loaded at /admin.
    window.history.replaceState(null, "");
    window.location.replace("/admin/");
  }, [atLoad, stay]);
  if (!stay) return <PageLoading />;
  return (
    <main className="grid h-full place-items-center">
      <p>管理端入口尚未配置</p>
    </main>
  );
}

// Storage that cannot be read reads as marked, so a page loaded at /admin never leaves unmarked.
function arrivedByAdminHop(): boolean {
  let markedAt: number;
  try {
    // A missing mark reads as 0, long ago.
    markedAt = Number(window.sessionStorage.getItem(ADMIN_HOP_KEY));
  } catch {
    return true;
  }
  // Compared with when this page started rather than now, since the session lookup before this
  // route can take any time.
  const startedAt = Date.now() - performance.now();
  return Math.abs(startedAt - markedAt) < ADMIN_HOP_WINDOW_MS;
}

function markAdminHop(): boolean {
  try {
    window.sessionStorage.setItem(ADMIN_HOP_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

function forgetAdminHop() {
  try {
    window.sessionStorage.removeItem(ADMIN_HOP_KEY);
  } catch {
    // Unreadable storage keeps every page loaded at /admin on the notice already.
  }
}

/**
 * A session lookup that never reached the server is not a sign-out, so the app
 * waits and retries here instead of routing to sign-in and stranding a signed-in
 * user. Better Auth only polls once a session exists, so the retry lives here.
 */
function SessionUnavailable({ refetch }: { refetch: () => Promise<void> }) {
  const { t } = useLingui();
  const [attempt, setAttempt] = useState(0);
  const [retryKey, setRetryKey] = useState(0);
  const retryImmediately = useRef(false);
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    let cancelled = false;
    const delay = retryImmediately.current ? 0 : sessionRetryDelayMs(attempt);
    retryImmediately.current = false;
    const timer = setTimeout(() => {
      void refetchRef.current().finally(() => {
        if (!cancelled) setAttempt((value) => value + 1);
      });
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [attempt, retryKey]);

  return (
    <div className="grid h-full place-items-center bg-background px-6 text-center">
      <div className="flex flex-col items-center">
        <LoadingState label={t`Reconnecting`} />
        <p className="mt-3 text-[13.5px] text-muted-foreground/80">
          <Trans>Can&apos;t reach the server.</Trans>
        </p>
        <div className="mt-4">
          <Button
            variant="secondary"
            onClick={() => {
              retryImmediately.current = true;
              setAttempt(0);
              setRetryKey((key) => key + 1);
            }}
          >
            <Trans>Retry now</Trans>
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Last resort for a render failure no inner boundary caught: offer a reload instead of a blank page. */
function AppFailed() {
  return (
    <div
      className="grid h-full place-items-center bg-background px-6 text-center"
      data-rakazo-app-state="failed"
    >
      <div className="flex flex-col items-center">
        <p className="text-[13.5px] text-muted-foreground/80">
          <Trans>Something went wrong. Try again.</Trans>
        </p>
        <div className="mt-4">
          <Button variant="secondary" onClick={() => window.location.reload()}>
            <Trans>Refresh</Trans>
          </Button>
        </div>
      </div>
    </div>
  );
}
